import React, {useCallback, useEffect, useMemo, useReducer, useRef} from 'react';
import {Auth0Provider} from "@auth0/auth0-react";
import {GetTokenSilentlyOptions, User} from "@auth0/auth0-spa-js";
import {reducer} from "./reducer.ts";
import {Auth0Client} from "./Client";
import {
    defaultOnRedirectCallback,
    hasAuthParams,
    initialAuthState,
    loginError,
    toAuth0ClientOptions, tokenError
} from "./helpers.ts";
import {Auth0ProviderOptions} from "./types.tsx";
import Auth0Context from "./context.tsx";
import {LogoutOptions} from "@auth0/auth0-react/src/auth0-context.tsx";

const Index = (props: Auth0ProviderOptions) => {
    const [state, dispatch] = useReducer(reducer, initialAuthState);
    const didInitialise = useRef(false);
    const {
        children,
        skipRedirectCallback,
        onRedirectCallback = defaultOnRedirectCallback,
        context = Auth0Context,
        ...clientOpts
    } = props;

    const [client] = React.useState(
        () => new Auth0Client(toAuth0ClientOptions(clientOpts))
    );
    useEffect(() => {
        if (didInitialise.current) {
            return;
        }
        didInitialise.current = true;
        (async (): Promise<void> => {
            try {
                let user: User | undefined;
                if (hasAuthParams() && !skipRedirectCallback) {
                    const {appState} = await client.handleRedirectCallback();
                    user = await client.getUser();
                    onRedirectCallback(appState, user);
                } else {
                    await client.checkSession();
                    user = await client.getUser();
                }
                dispatch({type: 'INITIALISED', user});
            } catch (error) {
                dispatch({type: 'ERROR', error: loginError(error)});
            }
        })();
    }, [client, onRedirectCallback, skipRedirectCallback]);

    const getAccessTokenSilently = useCallback(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        async (opts?: GetTokenSilentlyOptions): Promise<any> => {
            let token;
            try {
                token = await client.getTokenSilently(opts);
            } catch (error) {
                throw tokenError(error);
            } finally {
                dispatch({
                    type: 'HANDLE_REDIRECT_COMPLETE',
                    user: await client.getUser(),
                });
            }
            return token;
        },
        [client]
    );

    const logout = useCallback(
        async (opts: LogoutOptions = {}): Promise<void> => {
            await client.logout(opts);
            if (opts.openUrl || opts.openUrl === false) {
                dispatch({ type: 'LOGOUT' });
            }
        },
        [client]
    );

    const contextValue = useMemo(() => {
        return {
            ...state,
            getAccessTokenSilently,
            logout
        };
    }, [
        state,
        getAccessTokenSilently,
        logout
    ]);



    return <context.Provider value={contextValue}>{children}</context.Provider>;
};

export default window.location.href.toLowerCase().includes('mapp') ? Index : Auth0Provider;
